// SPDX-License-Identifier: GPL-3.0-only
// Input representation only. The selected arrangement is a weak graphical password:
// there are only 23 nonordinary choices. Native KDF/authentication does not expand this space.
export const OWNER_IDENTITY = "owner@vault.invalid";
export const COLOR_IDS = Object.freeze(["R", "Y", "B", "G"] as const);
export type RoomColor = (typeof COLOR_IDS)[number];
export interface RoomMove { color: RoomColor; cell: number }
export type RoomArrangement = Record<RoomColor, number>;
export const INITIAL_POSITIONS = Object.freeze({ R: 5, Y: 6, B: 9, G: 10 });

const CORNERS = Object.freeze([0, 3, 12, 15]);
const ORDINARY_FINISH = Object.freeze({ R: 0, Y: 3, B: 12, G: 15 });
const CANONICAL = /^vgc2\|R(00|03|12|15)\.Y(00|03|12|15)\.B(00|03|12|15)\.G(00|03|12|15)$/;

function invalidArrangement(): never {
  throw Object.assign(new Error("Invalid room arrangement"), { code: "invalid_room_arrangement" });
}
function randomUnavailable(): never {
  throw Object.assign(new Error("Browser random source unavailable"), { code: "random_unavailable" });
}
function validateArrangement(board: RoomArrangement): void {
  if (!board || typeof board !== "object" || Array.isArray(board) || Object.keys(board).length !== 4 ||
      !COLOR_IDS.every((color) => Object.prototype.hasOwnProperty.call(board, color) && Number.isInteger(board[color]) && CORNERS.includes(board[color])) ||
      new Set(COLOR_IDS.map((color) => board[color])).size !== 4 ||
      COLOR_IDS.every((color) => board[color] === ORDINARY_FINISH[color])) invalidArrangement();
}

/** Only the final color arrangement is encoded. Movement order/history has no effect. */
export function encodeArrangement(board: RoomArrangement): string {
  validateArrangement(board);
  return "vgc2|" + COLOR_IDS.map((color) => color + String(board[color]).padStart(2, "0")).join(".");
}
export function decodeArrangement(value: unknown): RoomArrangement | null {
  if (typeof value !== "string" || value.length !== 20) return null;
  const match = CANONICAL.exec(value);
  if (!match) return null;
  const board: RoomArrangement = { R: Number(match[1]), Y: Number(match[2]), B: Number(match[3]), G: Number(match[4]) };
  try { validateArrangement(board); return board; }
  catch { return null; }
}
/** Historical adapter name retained for compatibility; this validates an arrangement, not a route. */
export function isRoomRouteCredential(value: unknown): value is string {
  return decodeArrangement(value) !== null;
}

function personalArrangements(): RoomArrangement[] {
  const choices: RoomArrangement[] = [];
  const visit = (cells: number[]) => {
    if (cells.length === 4) {
      const board = { R: cells[0], Y: cells[1], B: cells[2], G: cells[3] };
      if (!COLOR_IDS.every((color) => board[color] === ORDINARY_FINISH[color])) choices.push(board);
      return;
    }
    for (const cell of CORNERS) if (!cells.includes(cell)) visit([...cells, cell]);
  };
  visit([]);
  return choices;
}

/** Optional, explicitly requested local generation; no shared correct arrangement is stored.
 * Discard bytes 253..255, leaving exactly eleven representatives for each of 23 choices.
 * CSPRNG selection is uniform but cannot turn this small password space into a strong password.
 */
export function generateArrangement(): RoomArrangement {
  if (!globalThis.crypto || typeof globalThis.crypto.getRandomValues !== "function") randomUnavailable();
  const choices = personalArrangements();
  if (choices.length !== 23) invalidArrangement();
  const byte = new Uint8Array(1);
  for (let attempt = 0; attempt < 256; attempt++) {
    try { globalThis.crypto.getRandomValues(byte); }
    catch { randomUnavailable(); }
    const value = byte[0]; byte[0] = 0;
    if (value < 253) return { ...choices[value % 23] };
  }
  randomUnavailable();
}
